import React, { useMemo, useState } from "react";
import BlogsCard from "./BlogsCard";
import type { DisplayPost } from "./blogTypes";

// Category pills only show up when the posts actually carry a category (the static fallback
// data does; the real Ghost CMS feed doesn't) -- no fabricated categories for real posts.
const BlogsGrid: React.FC<{ posts: DisplayPost[] }> = ({ posts }) => {
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState<string | "All">("All");

  const categories = useMemo(() => {
    const set = new Set(posts.map((p) => p.category).filter((c): c is string => Boolean(c)));
    return Array.from(set);
  }, [posts]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return posts
      .filter((p) => activeCategory === "All" || p.category === activeCategory)
      .filter(
        (p) =>
          !q ||
          p.title.toLowerCase().includes(q) ||
          (p.excerpt ?? "").toLowerCase().includes(q)
      );
  }, [posts, query, activeCategory]);

  return (
    <section className="bp-grid">
      <div className="bp-grid__toolbar">
        {categories.length > 0 && (
          <div className="bp-grid__categories">
            {(["All", ...categories] as const).map((c) => (
              <button
                key={c}
                type="button"
                className={activeCategory === c ? "active" : ""}
                onClick={() => setActiveCategory(c)}
              >
                {c}
              </button>
            ))}
          </div>
        )}
        <div className="bp-grid__search">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.5-3.5" />
          </svg>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search articles…"
          />
        </div>
      </div>

      <div className="bp-grid__list">
        {filtered.map((post) => (
          <BlogsCard key={post.slug} post={post} />
        ))}
        {filtered.length === 0 && (
          <div className="bp-grid__empty">No articles matched. Try a different keyword.</div>
        )}
      </div>
    </section>
  );
};

export default BlogsGrid;
